import { useState, useEffect } from 'react';
import {
  X,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Gift,
  TrendingUp,
  Users,
  Check,
} from 'lucide-react';

import { cn } from '@/lib/utils';

interface TutorialModalProps {
  open: boolean;
  onClose: () => void;
  onComplete?: () => void;
}

interface TutorialStep {
  id: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  tips?: string[];
  highlight?: string;
}

export function TutorialModal({
  open,
  onClose,
  onComplete,
}: TutorialModalProps) {
  const [currentStep, setCurrentStep] = useState(0);

  const steps: TutorialStep[] = [
    {
      id: 0,
      title: 'Welcome to Junk.Fun!',
      description:
        'Turn your worthless tokens and NFTs into real credits rewards. This quick tutorial will show you how everything works.',
      icon: <Gift size={48} style={{ color: '#50E4F9' }} />,
      highlight: 'Ready to clean up your wallet and earn rewards?',
    },
    {
      id: 1,
      title: 'Junk Your Tokens',
      description:
        "Select worthless tokens or NFTs from your wallet and burn them to reclaim credits rent. You'll also earn points for each burn!",
      icon: <Trash2 size={48} style={{ color: '#FB8B00' }} />,
      tips: [
        'Connect your wallet to see all your tokens and NFTs',
        'Select multiple items at once to junk them together',
        'Each burn earns you points and credits rent back',
        'Protected tokens (USDC, credits, etc.) cannot be burned',
      ],
    },
    {
      id: 2,
      title: 'Open Treasure Chests',
      description:
        'Earned chests from burning? Open them for a chance to win credits prizes! There are 8 probability tiers with different rewards.',
      icon: <Gift size={48} style={{ color: '#FEBB01' }} />,
      tips: [
        'Each chest costs 0.001 credits to open',
        'Win instant credits prizes from 8 different tiers',
        'Higher tiers unlock as more chests are opened globally',
        'Check Prize Tiers to see all possible rewards',
      ],
    },
    {
      id: 3,
      title: 'Points Lottery',
      description:
        'Invest your points in seasonal lottery competitions for a chance to win big credits prizes. More tiers unlock as players contribute!',
      icon: <TrendingUp size={48} style={{ color: '#9D4EDD' }} />,
      tips: [
        'Seasons run for limited time periods',
        'Contribute points to unlock higher prize tiers',
        'Progressive unlocking - more participation = better rewards',
        'Minimum contribution: 100 points',
      ],
    },
    {
      id: 4,
      title: 'Earn More Points',
      description:
        'Points are earned by junking tokens, opening chests, and referring friends. Use them for the Points Lottery!',
      icon: <TrendingUp size={48} style={{ color: '#22C55E' }} />,
      tips: [
        'Junk tokens: +100 points per burn',
        'Open chests: +500 points per chest',
        'Refer friends: Earn bonus points when they participate',
        'Social actions: Follow on Twitter, join Telegram for bonus points',
      ],
    },
    {
      id: 5,
      title: 'Invite Friends & Earn',
      description:
        'Share your referral code with friends. When they junk tokens or open chests, you both earn bonus points!',
      icon: <Users size={48} style={{ color: '#50E4F9' }} />,
      tips: [
        'Get your unique invite code from the Refer page',
        'Share it with friends to earn referral bonuses',
        'Earn points when your referrals participate',
        'Claim your rewards anytime from the Refer page',
      ],
    },
    {
      id: 6,
      title: 'Ready to Start!',
      description:
        "You're all set! Start by connecting your wallet, then junk some tokens to earn your first points and rewards.",
      icon: <Check size={48} style={{ color: '#22C55E' }} />,
      highlight: 'Time to clean up your wallet and start earning! 🚀',
    },
  ];

  const totalSteps = steps.length;
  const currentStepData = steps[currentStep];
  const progress = ((currentStep + 1) / totalSteps) * 100;

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    if (onComplete) {
      onComplete();
    }
    onClose();
  };

  const handleSkip = () => {
    if (onComplete) {
      onComplete();
    }
    onClose();
  };

  // Reset to step 0 when modal opens
  useEffect(() => {
    if (open) {
      setCurrentStep(0);
    }
  }, [open]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleSkip();
      } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        handlePrevious();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentStep]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={handleSkip}
      />

      {/* Modal */}
      <div
        className="animate-scale-in relative w-full max-w-2xl rounded-xl shadow-2xl"
        style={{
          backgroundColor: '#1a1a1a',
          border: '2px solid #50E4F9',
          boxShadow: '0 0 40px rgba(80, 228, 249, 0.4)',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between border-b p-6"
          style={{ borderColor: 'rgba(80, 228, 249, 0.3)' }}
        >
          <div className="flex items-center gap-3">
            <div
              className="flex h-12 w-12 items-center justify-center rounded-full"
              style={{ backgroundColor: 'rgba(80, 228, 249, 0.1)' }}
            >
              {currentStepData.icon}
            </div>
            <div>
              <h2
                className="text-2xl font-bold text-white"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {currentStepData.title}
              </h2>
              <p
                className="text-sm"
                style={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontFamily: 'Outfit, sans-serif',
                }}
              >
                Step {currentStep + 1} of {totalSteps}
              </p>
            </div>
          </div>
          <button
            onClick={handleSkip}
            className="rounded-full p-2 transition-colors"
            style={{ color: 'rgba(255, 255, 255, 0.6)' }}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor =
                'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.color = '#FFFFFF';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)';
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="h-1 w-full bg-gray-800">
          <div
            className="h-full transition-all duration-300"
            style={{
              width: `${progress}%`,
              background: 'linear-gradient(90deg, #50E4F9 0%, #9D4EDD 100%)',
            }}
          />
        </div>

        {/* Content */}
        <div className="space-y-6 p-8">
          {/* Description */}
          <p
            className="text-lg leading-relaxed text-white"
            style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400 }}
          >
            {currentStepData.description}
          </p>

          {/* Highlight Box */}
          {currentStepData.highlight && (
            <div
              className="rounded-lg p-4"
              style={{
                backgroundColor: 'rgba(80, 228, 249, 0.1)',
                border: '2px solid #50E4F9',
              }}
            >
              <p
                className="text-center font-semibold"
                style={{
                  color: '#50E4F9',
                  fontFamily: 'Outfit, sans-serif',
                  fontWeight: 600,
                }}
              >
                {currentStepData.highlight}
              </p>
            </div>
          )}

          {/* Tips */}
          {currentStepData.tips && currentStepData.tips.length > 0 && (
            <div
              className="rounded-lg p-5"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <h3
                className="mb-3 text-sm font-bold text-white"
                style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700 }}
              >
                KEY POINTS:
              </h3>
              <ul className="space-y-2">
                {currentStepData.tips.map((tip, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div
                      className="mt-2 h-2 w-2 flex-shrink-0 rounded-full"
                      style={{ backgroundColor: '#50E4F9' }}
                    />
                    <span
                      className="text-sm leading-relaxed"
                      style={{
                        color: 'rgba(255, 255, 255, 0.9)',
                        fontFamily: 'Outfit, sans-serif',
                        fontWeight: 400,
                      }}
                    >
                      {tip}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Footer Navigation */}
        <div
          className="font-base flex items-center justify-between border-t p-6"
          style={{ borderColor: 'rgba(80, 228, 249, 0.3)' }}
        >
          {/* Previous Button */}
          <button
            onClick={handlePrevious}
            disabled={currentStep === 0}
            className="flex items-center gap-2 rounded-lg px-6 py-3 transition-all disabled:cursor-not-allowed disabled:opacity-30 max-md:mr-2 max-md:px-3"
            style={{
              fontFamily: 'Outfit, sans-serif',
              fontWeight: 600,
              backgroundColor:
                currentStep === 0 ? 'transparent' : 'rgba(255, 255, 255, 0.1)',
              color: '#FFFFFF',
            }}
            onMouseEnter={e => {
              if (currentStep !== 0) {
                e.currentTarget.style.backgroundColor =
                  'rgba(255, 255, 255, 0.2)';
              }
            }}
            onMouseLeave={e => {
              if (currentStep !== 0) {
                e.currentTarget.style.backgroundColor =
                  'rgba(255, 255, 255, 0.1)';
              }
            }}
          >
            <ChevronLeft size={20} />
            <span className="max-md:hidden">PREVIOUS</span>
          </button>

          {/* Step Indicators */}
          <div className="flex gap-2">
            {steps.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentStep(index)}
                className={cn(
                  'h-2 w-2 rounded-full transition-all',
                  index === currentStep ? 'max-md:w-2 md:w-6' : 'w-2'
                )}
                style={{
                  backgroundColor:
                    index === currentStep
                      ? '#50E4F9'
                      : 'rgba(255, 255, 255, 0.3)',
                }}
              />
            ))}
          </div>

          {/* Next/Finish Button */}
          <button
            onClick={handleNext}
            className="flex transform items-center gap-2 rounded-lg px-6 py-3 transition-all hover:scale-105 max-md:px-3"
            style={{
              fontFamily: 'Outfit, sans-serif',
              fontWeight: 600,
              backgroundColor: '#50E4F9',
              color: '#000000',
              boxShadow: '0 0 20px rgba(80, 228, 249, 0.4)',
            }}
          >
            {currentStep === totalSteps - 1 ? (
              <>
                LET'S GO!
                <Check size={20} />
              </>
            ) : (
              <>
                NEXT
                <ChevronRight size={20} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
